import { cn } from '@/lib/cn';
import { Icons } from '@/components/icons';
import { parseDate } from '@internationalized/date';
import { DateTimePicker } from '../form/dynamic-field/components/date-field/date-time-picker';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  BOOLEAN_FILTER,
  DATE_FILTER,
  DATE_WINDOW_PRESETS,
  NUMBER_FILTER,
  SELECT_FILTER,
  STRING_FILTER,
  isRelativeDateOperator,
  type Filter,
  type FilterOperator,
  type FilterValue,
} from './constants';

interface FilterValueInputProps {
  filter: Filter;
  operator: FilterOperator;
  value: FilterValue;
  setValue: (val: FilterValue) => void;
  onDone?: VoidFunction;
}
/*
    This component handles all inputs that aren't relations, 
    thats handled by the relation modal instead
*/
export function FilterValueInput({
  filter,
  operator,
  value,
  setValue,
  onDone,
}: FilterValueInputProps) {
  switch (filter.kind) {
    case STRING_FILTER: {
      if (typeof value !== 'boolean') {
        return (
          <Input
            placeholder={filter.title + '...'}
            onKeyDown={(event) => event.key === 'Enter' && onDone?.()}
            value={value ?? ''}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        );
      }
      break;
    }
    case DATE_FILTER: {
      if (isRelativeDateOperator(operator)) {
        return (
          <div className='flex flex-col items-center gap-2'>
            <Input
              placeholder='Ange antal dagar...'
              onKeyDown={(event) => event.key === 'Enter' && onDone?.()}
              value={
                value && typeof value === 'string'
                  ? value
                  : (value?.toString() ?? '')
              }
              min={1}
              type='number'
              onChange={(event) => {
                const raw = event.target.value;
                if (raw === '') {
                  setValue(null);
                  return;
                }
                const n = Number(raw);
                if (!Number.isFinite(n)) return;
                setValue(String(Math.max(1, Math.round(n))));
              }}
            />
            <Select onValueChange={(value) => setValue(value)}>
              <SelectTrigger className='h-9 min-h-9'>
                <SelectValue placeholder='Välj period' />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DATE_WINDOW_PRESETS).map(([key, value]) => (
                  <SelectItem key={key} value={key}>
                    {value.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      }
      return (
        <DateTimePicker
          label='Filtrera på datum'
          hideResetButton={true}
          value={value && typeof value === 'string' ? parseDate(value) : null}
          onChange={(val) => {
            if (val) {
              const date = val.toString();

              if (date === value) {
                onDone?.();
              } else {
                setValue(date);
              }
            }
          }}
        />
      );
    }
    case NUMBER_FILTER: {
      if (typeof value !== 'boolean') {
        return (
          <Input
            onKeyDown={(event) => event.key === 'Enter' && onDone?.()}
            value={value ?? ''}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        );
      }
      break;
    }

    case BOOLEAN_FILTER: {
      return (
        <Command>
          <CommandList>
            <CommandGroup className='max-h-100 overflow-y-scroll'>
              <CommandItem
                onSelect={() => {
                  setValue(true);
                }}
              >
                <Icons.check
                  className={cn(
                    'mr-2 size-4 opacity-0 transition-opacity',
                    value && 'opacity-100'
                  )}
                />
                Sant
              </CommandItem>
              <CommandItem
                onSelect={() => {
                  setValue(false);
                }}
              >
                <Icons.check
                  className={cn(
                    'mr-2 size-4 opacity-0 transition-opacity',
                    value === false && 'opacity-100'
                  )}
                />
                Falskt
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      );
    }

    case SELECT_FILTER: {
      if (!Array.isArray(value) && value != null) {
        break;
      }
      return (
        <Command>
          <CommandInput placeholder='Sök bland val...' />
          <CommandList>
            <CommandEmpty>Hittade inga alternativ.</CommandEmpty>
            <CommandGroup className='max-h-100 overflow-y-scroll'>
              {filter.choices?.map((c) => {
                const selected = (value ?? []).includes(c.v.toString());
                return (
                  <CommandItem
                    onSelect={() => {
                      if (selected) {
                        setValue(
                          (value || []).filter((v) => v != c.v.toString())
                        );
                      } else {
                        setValue([...(value || []), c.v.toString()]);
                      }
                    }}
                    key={c.v?.toString()}
                  >
                    <Icons.check
                      className={cn(
                        'mr-2 size-4 opacity-0 transition-opacity',
                        selected && 'opacity-100'
                      )}
                    />
                    {c.d}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      );
    }
  }
}
